import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown, { Components } from 'react-markdown';
import { Calendar, Clock, ArrowLeft, User, Tag, Zap, CheckCircle } from 'lucide-react';
import { loadBlogPosts } from '../utils/blogLoader';
import { BlogPost as BlogPostType } from '../types';
import GoogleAd from '../components/GoogleAd';

const headingClasses = {
  1: "text-3xl font-bold text-gray-900 dark:text-white mb-6 mt-8",
  2: "text-2xl font-bold text-gray-900 dark:text-white mb-4 mt-6",
  3: "text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-5",
  4: "text-lg font-semibold text-gray-900 dark:text-white mb-2 mt-4",
  5: "text-base font-semibold text-gray-900 dark:text-white mb-2 mt-3",
  6: "text-sm font-semibold text-gray-900 dark:text-white mb-2 mt-3"
};

const markdownComponents: Components = {
  h1: ({ children }) => <h1 className={headingClasses[1]}>{children}</h1>,
  h2: ({ children }) => <h2 className={headingClasses[2]}>{children}</h2>,
  h3: ({ children }) => <h3 className={headingClasses[3]}>{children}</h3>,
  h4: ({ children }) => <h4 className={headingClasses[4]}>{children}</h4>,
  h5: ({ children }) => <h5 className={headingClasses[5]}>{children}</h5>,
  h6: ({ children }) => <h6 className={headingClasses[6]}>{children}</h6>,
  p: ({ children }) => <p className="text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">{children}</p>,
  ul: ({ children }) => <ul className="list-disc ml-6 mb-4">{children}</ul>,
  ol: ({ children, start }) => <ol start={start} className="list-decimal ml-6 mb-4">{children}</ol>,
  li: ({ children }) => <li className="text-gray-700 dark:text-gray-300 mb-2">{children}</li>,
  strong: ({ children }) => <strong className="font-bold text-gray-900 dark:text-white">{children}</strong>,
  blockquote: ({ children }) => (
    <blockquote className="not-prose border-l-4 border-blue-500 bg-blue-50 dark:bg-blue-900/20 rounded-r-lg px-4 py-3 my-6 [&_p]:mb-0">
      {children}
    </blockquote>
  ),
  a: ({ href = '', children }) =>
    href.startsWith('/') ? (
      <Link to={href} className="text-blue-600 dark:text-blue-400 underline">{children}</Link>
    ) : (
      <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 underline">{children}</a>
    )
};

const renderContent = (content: string) => (
  <ReactMarkdown components={markdownComponents}>{content}</ReactMarkdown>
);

// Related post card component
const RelatedPostCard: React.FC<{ post: BlogPostType }> = ({ post }) => {
  return (
    <Link to={`/blog/${post.slug}`} className="block group">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md h-full border border-gray-100 dark:border-gray-700">
        <div className="h-40 overflow-hidden">
          <img 
            src={post.imageUrl} 
            alt={post.title} 
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="p-4">
          {post.categories && post.categories.length > 0 && (
            <span className="inline-block px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs font-semibold mb-2">
              {post.categories[0]}
            </span>
          )}
          <h3 className="font-bold text-base mb-2 text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
            {post.title}
          </h3>
          <div className="flex items-center text-gray-500 dark:text-gray-400 text-xs mt-2">
            <Clock className="h-3 w-3 mr-1" />
            <span>{post.readTime} min</span>
          </div>
        </div>
      </div>
    </Link>
  );
};

// Main BlogPost page component
const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = React.useState<BlogPostType | null>(null);
  const [relatedPosts, setRelatedPosts] = React.useState<BlogPostType[]>([]);
  const [loading, setLoading] = React.useState(true);
  
  React.useEffect(() => {
    const loadPost = async () => {
      if (!slug) {
        navigate('/blog');
        return;
      }

      try {
        const posts = await loadBlogPosts();
        const currentPost = posts.find(p => p.slug === slug);
        
        if (!currentPost) {
          navigate('/blog');
          return;
        }

        setPost(currentPost);
        
        // Get related posts based on categories
        const related = posts
          .filter(p => p.id !== currentPost.id && 
                      p.categories.some(cat => currentPost.categories.includes(cat)))
          .slice(0, 3);
        setRelatedPosts(related);
      } catch (error) {
        console.error('Failed to load blog post:', error);
        navigate('/blog');
      } finally {
        setLoading(false);
      }
    };

    loadPost();
  }, [slug, navigate]);
  
  if (loading) {
    return (
      <main className="flex-grow container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 dark:border-blue-400 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-300">Laster blogginnlegg...</p>
          </div>
        </div>
      </main>
    );
  }
  
  if (!post) {
    return null;
  }

  // Format dates for structured data
  const publishDate = post.publishDate.replace(/(\d+)\. (\w+) (\d+)/, '$3-$2-$1');
  const modifiedDate = post.updatedDate ? post.updatedDate.replace(/(\d+)\. (\w+) (\d+)/, '$3-$2-$1') : publishDate;
  
  // Build canonical URL
  const canonicalUrl = `https://stromkalkulator.no/blog/${post.slug}`;
  
  return (
    <>
      <Helmet>
        <title>{`${post.title} | Strømkalkulator.no`}</title>
        <meta name="description" content={post.excerpt} />
        <meta name="keywords" content={post.tags?.join(', ') || post.categories?.join(', ') || ''} />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph meta tags for social sharing */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:image" content={post.coverImage} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="Strømkalkulator.no" />
        <meta property="article:published_time" content={publishDate} />
        {post.updatedDate && <meta property="article:modified_time" content={modifiedDate} />}
        <meta property="article:author" content={post.author} />
        <meta property="article:section" content={post.category} />
        {post.tags?.map((tag, i) => (
          <meta property="article:tag" content={tag} key={i} />
        ))}
        
        {/* Twitter Card data */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt} />
        <meta name="twitter:image" content={post.coverImage} />
        
        {/* Schema.org structured data for Article */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": canonicalUrl
            },
            "headline": post.title,
            "image": post.coverImage,
            "datePublished": publishDate,
            "dateModified": modifiedDate,
            "author": {
              "@type": "Person",
              "name": post.author
            },
            "publisher": {
              "@type": "Organization",
              "name": "Strømkalkulator.no",
              "logo": {
                "@type": "ImageObject",
                "url": "https://stromkalkulator.no/logo.png",
                "width": 60,
                "height": 60
              }
            },
            "description": post.excerpt
        }) }} />
        
        {/* BreadcrumbList structured data */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Hjem",
                "item": "https://stromkalkulator.no"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Blog",
                "item": "https://stromkalkulator.no/blog"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": post.title,
                "item": canonicalUrl
              }
            ]
          }) }} />
      </Helmet>

      <main className="flex-grow container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Back to blog link */}
          <Link to="/blog" className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-6">
            <ArrowLeft className="h-4 w-4 mr-1" />
            <span>Tilbake til alle artikler</span>
          </Link>
          
          {/* Article header */}
          <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden mb-10 border border-gray-200 dark:border-gray-700">
            <div className="h-80 relative">
              <img 
                src={post.imageUrl} 
                alt={post.title} 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-8">
                {post.categories && post.categories.length > 0 && (
                  <span className="inline-block px-3 py-1 rounded-full bg-blue-600 text-white text-sm font-semibold mb-3">
                    {post.categories[0]}
                  </span>
                )}
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">{post.title}</h1>
                <div className="flex flex-wrap items-center text-white/80 text-sm gap-y-2">
                  <div className="flex items-center mr-6">
                    <User className="h-4 w-4 mr-1" />
                    <span>{post.author}</span>
                  </div>
                  <div className="flex items-center mr-6">
                    <Calendar className="h-4 w-4 mr-1" />
                    <span>{new Date(post.publishDate).toLocaleDateString('nb-NO')}</span>
                  </div>
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    <span>{post.readTime} min lesetid</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Article excerpt */}
            <div className="p-8 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-800">
              <p className="text-lg text-gray-700 dark:text-gray-300 font-medium italic">{post.excerpt}</p>
              
              <div className="flex flex-wrap gap-2 mt-4">
                {post.tags && post.tags.map((tag, index) => (
                  <span key={index} className="px-3 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs font-medium">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            
            {/* Ad placement before article content */}
            <div className="p-8 border-b border-gray-100 dark:border-gray-700">
              <GoogleAd 
                slot="3068465121"
                style={{ 
                  margin: '0 auto',
                  maxWidth: '728px',
                  minHeight: '90px'
                }} 
              />
            </div>
            
            {/* Article content */}
            <div className="p-8 prose prose-lg dark:prose-invert max-w-none">
              {renderContent(post.content)}
              
              {/* Tibber affiliate section for relevant posts */}
              {(post.slug.includes('stromstotte') || 
                post.slug.includes('norgespris') ||
                post.categories.some(cat => cat.toLowerCase().includes('strøm'))) && (
                <div className="not-prose my-8 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-6 border-2 border-green-200 dark:border-green-800">
                  <div className="flex justify-end mb-2">
                    <span className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide">Reklame</span>
                  </div>
                  <div className="flex flex-col lg:flex-row items-center justify-between">
                    <div className="flex-1 mb-4 lg:mb-0 lg:mr-6">
                      <div className="flex items-center mb-3">
                        <div className="bg-white dark:bg-gray-800 p-3 rounded-lg mr-3 shadow-sm">
                          <img 
                            src="https://framerusercontent.com/images/Wo8WMNjuYZXvq6jz7bPEhLZuv4w.png" 
                            alt="Tibber logo" 
                            className="h-12 w-12 rounded-lg object-cover"
                          />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                            Tibber - Smart strøm for fremtiden
                          </h3>
                          <p className="text-sm text-green-700 dark:text-green-300 font-medium">
                            AI-optimering og smartlading
                          </p>
                        </div>
                      </div>
                      <p className="text-sm text-green-800 dark:text-green-200 mb-3">
                        🤖 <strong>SMART TEKNOLOGI</strong> - AI optimerer ditt strømforbruk automatisk! Perfekt for elbileiere og smarthjem.
                      </p>
                    </div>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        window.open('https://go.adt284.net/t/t?a=1825795682&as=1963402684&t=2&tk=1', '_blank', 'noopener,noreferrer');
                      }}
                      className="inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-bold shadow-lg"
                    >
                      <Zap className="w-5 h-5 mr-2" />
                      Prøv Tibber
                    </a>
                  </div>
                </div>
              )}
            </div>
            
            {/* Article tags and share options */}
            <div className="p-8 bg-gray-50 dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700">
              <div className="flex flex-wrap items-center justify-between">
                <div className="flex items-center flex-wrap gap-2 mb-4 sm:mb-0">
                  <Tag className="h-4 w-4 text-gray-400 dark:text-gray-500 mr-1" />
                  <span className="text-sm text-gray-600 dark:text-gray-300 mr-2">Emner:</span>
                  {post.tags ? (
                    post.tags.map((tag, index) => (
                      <span key={index} className="px-3 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs font-medium">
                        {tag}
                      </span>
                    ))
                  ) : (
                    <span className="px-3 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-xs font-medium">
                      {post.categories[0]}
                    </span>
                  )}
                </div>
                
                <div className="flex gap-2">
                  <button 
                    className="p-2 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                    title="Del på Facebook"
                    onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${canonicalUrl}`, '_blank')}
                    aria-label="Del på Facebook"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </button>
                  <button 
                    className="p-2 rounded-full bg-blue-400 text-white hover:bg-blue-500 transition-colors"
                    title="Del på Twitter"
                    onClick={() => window.open(`https://twitter.com/intent/tweet?url=${canonicalUrl}&text=${post.title}`, '_blank')}
                    aria-label="Del på Twitter"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                    </svg>
                  </button>
                  <button 
                    className="p-2 rounded-full bg-gray-600 text-white hover:bg-gray-700 transition-colors"
                    title="Kopier lenke"
                    onClick={() => {
                      navigator.clipboard.writeText(canonicalUrl);
                      alert('Lenke kopiert til utklippstavlen!');
                    }}
                    aria-label="Kopier lenke"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </article>
          
          {/* Ad placement after article content */}
          <div className="my-8">
            <GoogleAd 
              slot="3068465121"
              style={{ 
                margin: '0 auto',
                maxWidth: '728px',
                minHeight: '90px'
              }} 
            />
          </div>
          
          {/* Related articles */}
          {relatedPosts.length > 0 && (
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Relaterte artikler</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedPosts.map(relatedPost => (
                  <RelatedPostCard key={relatedPost.id} post={relatedPost} />
                ))}
              </div>
            </div>
          )}
          
          {/* Call to action */}
          <div className="mb-8 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-6 border border-green-200 dark:border-green-800">
            <div className="flex justify-end mb-2">
              <span className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide">Reklame</span>
            </div>
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm">
                  <img 
                    src="https://framerusercontent.com/images/Wo8WMNjuYZXvq6jz7bPEhLZuv4w.png" 
                    alt="Tibber logo" 
                    className="h-16 w-16 rounded-lg object-cover"
                  />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Spar penger med Tibber
              </h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Europas mest innovative strømselskap med AI-teknologi som optimerer ditt forbruk automatisk.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6 text-sm">
                <div className="flex items-center justify-center text-green-700 dark:text-green-300">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  <span>Smart app</span>
                </div>
                <div className="flex items-center justify-center text-green-700 dark:text-green-300">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  <span>AI-optimering</span>
                </div>
                <div className="flex items-center justify-center text-green-700 dark:text-green-300">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  <span>Smartlading</span>
                </div>
              </div>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  window.open('https://go.adt284.net/t/t?a=1825795682&as=1963402684&t=2&tk=1', '_blank', 'noopener,noreferrer');
                }}
                className="inline-flex items-center justify-center px-8 py-4 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors font-bold text-lg shadow-lg"
              >
                <Zap className="w-6 h-6 mr-2" />
                Prøv Tibber nå
              </a>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-700 dark:to-blue-800 text-white rounded-xl p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">Spar penger på strømregningen din</h2>
            <p className="mb-6">
              Bruk vår strømkalkulator for å finne de billigste timene å bruke strøm og få en bedre oversikt over dine kostnader.
            </p>
            <Link 
              to="/"
              className="inline-block px-6 py-3 bg-white text-blue-700 font-medium rounded-lg hover:bg-blue-50 dark:hover:bg-gray-100 transition-colors"
            >
              Gå til strømkalkulatoren
            </Link>
          </div>
        </div>
      </main>
    </>
  );
};

export default BlogPost;